///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Saved views sync orchestrator. Local-first, last-write-wins:
///
/// 1. GET the server delta since lastSyncedAt (tombstones included)
/// 2. Decide which local views/deletions are newer and must be pushed
/// 3. Merge the server delta into the local slice (LWW per view)
/// 4. Push local winners (PUT) and tombstones (DELETE)
/// 5. Advance lastSyncedAt to server_time only if every push succeeded,
///    so failed pushes are retried on the next sync
///
/// On first sign-in lastSyncedAt is null, so all anonymous local views
/// are pushed up automatically (ids are UUIDs, no merge conflicts).
///

import type { SavedViewRecord } from "@shared-types"
import { savedViewsApi, viewToPutRequest } from "./savedViewsApi"
import { markSynced, mergeServerViews, removeTombstones, type SavedView } from "./savedViewsSlice"
import { appStore } from "./store"

let syncInFlight = false

/** Convert a server record to the client SavedView shape */
function recordToView(record: SavedViewRecord): SavedView {
	return {
		id: record.id,
		name: record.name,
		camera: record.camera,
		focusTargetId: record.focus_target_id ?? undefined,
		layers: record.layers.length > 0 ? record.layers : undefined,
		tags: record.tags,
		createdAt: record.created_at,
		updatedAt: record.updated_at,
	}
}

/**
 * Run a full delta sync. Safe to call at any time: no-ops when signed
 * out or when a sync is already running. Failures leave the local state
 * intact and are retried on the next trigger (sign-in, reconnect, next
 * app start).
 */
export async function syncSavedViews(): Promise<void> {
	if (syncInFlight) return
	if (!appStore.getState().user.userProfile) return

	syncInFlight = true
	try {
		const since = appStore.getState().savedViews.lastSyncedAt

		// 1. Fetch the server delta
		const subscription = appStore.dispatch(
			savedViewsApi.endpoints.getSavedViewsDelta.initiate({ since }, { forceRefetch: true })
		)
		let delta
		try {
			delta = await subscription.unwrap()
		} finally {
			subscription.unsubscribe()
		}

		// 2. Decide pushes from the pre-merge snapshot
		const local = appStore.getState().savedViews
		const serverById = new Map(delta.views.map(record => [record.id, record]))

		const pushes = Object.values(local.views).filter(view => {
			const server = serverById.get(view.id)
			// Server copy (or deletion) is same-or-newer: server wins
			if (server && server.updated_at >= view.updatedAt) return false
			// Changed since the last sync, or never synced, or newer than
			// the server copy: push it up
			return since === null || view.updatedAt > since || server !== undefined
		})
		const deletions = Object.keys(local.deleted).filter(id => {
			const server = serverById.get(id)
			// Server re-created the view after our deletion: server wins
			return !(server && !server.deleted_at && server.updated_at >= local.deleted[id])
		})

		// 3. Merge the server delta locally (LWW per view)
		appStore.dispatch(mergeServerViews({
			views: delta.views.filter(record => !record.deleted_at).map(recordToView),
			tombstones: delta.views
				.filter(record => record.deleted_at)
				.map(record => ({ id: record.id, deletedAt: record.deleted_at! })),
		}))

		// 4. Push local winners
		let allPushesSucceeded = true

		for (const view of pushes) {
			try {
				await appStore.dispatch(
					savedViewsApi.endpoints.putSavedView.initiate({ id: view.id, body: viewToPutRequest(view) })
				).unwrap()
			} catch (error) {
				console.warn(`[savedViewsSync] Failed to push view ${view.id}:`, error)
				allPushesSucceeded = false
			}
		}

		const deletedOnServer: string[] = []
		for (const id of deletions) {
			try {
				await appStore.dispatch(savedViewsApi.endpoints.deleteSavedView.initiate(id)).unwrap()
				deletedOnServer.push(id)
			} catch (error) {
				console.warn(`[savedViewsSync] Failed to push deletion ${id}:`, error)
				allPushesSucceeded = false
			}
		}
		if (deletedOnServer.length > 0) {
			appStore.dispatch(removeTombstones(deletedOnServer))
		}

		// 5. Advance the cursor only when everything reached the server
		if (allPushesSucceeded) {
			appStore.dispatch(markSynced(delta.server_time))
		}
	} catch (error) {
		console.warn("[savedViewsSync] Sync failed (will retry on next trigger):", error)
	} finally {
		syncInFlight = false
	}
}
